import { z } from 'zod';

// TODO: refine after Data Structure doc review

export const StudioRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer']);
export type StudioRole = z.infer<typeof StudioRoleSchema>;

// Stored values are snake_case to match the Postgres CHECK constraint literals.
// Display labels live in TEAM_SIZE_LABELS and USE_CASE_LABELS below.
export const TeamSizeSchema = z.enum(['just_me', '2_5', '6_20', '21_50', '50_plus']);
export type TeamSize = z.infer<typeof TeamSizeSchema>;

export const UseCaseSchema = z.enum(['pre_release', 'post_release', 'ideating', 'exploring']);
export type UseCase = z.infer<typeof UseCaseSchema>;

// Display labels for UI (British spelling). Centralised so the portal and any
// future admin tools render identically. Stored values stay snake_case.
export const TEAM_SIZE_LABELS: Readonly<Record<TeamSize, string>> = {
  just_me: 'Just me',
  '2_5': '2-5',
  '6_20': '6-20',
  '21_50': '21-50',
  '50_plus': '50+',
};

export const USE_CASE_LABELS: Readonly<Record<UseCase, string>> = {
  pre_release: 'Pre-release testing',
  post_release: 'Post-release testing',
  ideating: 'Ideating and concepting',
  exploring: 'Just exploring',
};

export const StudioSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z.string().min(1),
  // Opaque, stable URL identifier (migration 0113). Unlike slug it is not
  // derived from the name, so it never drifts when the studio is renamed.
  shortId: z.string().min(1),
  teamSize: TeamSizeSchema,
  useCase: UseCaseSchema,
  stripeCustomerId: z.string().nullable(),
  subscriptionStatus: z.enum(['trialing', 'active', 'past_due', 'canceled', 'incomplete']).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Studio = z.infer<typeof StudioSchema>;

export const StudioMemberSchema = z.object({
  id: z.string().uuid(),
  studioId: z.string().uuid(),
  userId: z.string().uuid(),
  role: StudioRoleSchema,
  invitedAt: z.string().datetime({ offset: true }).nullable(),
  joinedAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type StudioMember = z.infer<typeof StudioMemberSchema>;

// Roles the Members > Edit members popover offers. The DB column still allows
// admin/viewer for legacy/future use, but the portal currently only produces
// owner/member - and update_studio_member_role enforces the same restriction.
export const EditableStudioRoleSchema = z.enum(['owner', 'member']);
export type EditableStudioRole = z.infer<typeof EditableStudioRoleSchema>;

// update_studio_member_role response envelope.
export const StudioMemberRoleUpdateResponseSchema = z.object({
  memberId: z.string().uuid(),
  role: StudioRoleSchema,
  changed: z.boolean(),
});
export type StudioMemberRoleUpdateResponse = z.infer<
  typeof StudioMemberRoleUpdateResponseSchema
>;

// revoke_studio_member response envelope.
export const StudioMemberRevokeResponseSchema = z.object({
  memberId: z.string().uuid(),
  studioId: z.string().uuid(),
});
export type StudioMemberRevokeResponse = z.infer<
  typeof StudioMemberRevokeResponseSchema
>;
